import { ref, computed } from 'vue';
import { computeMonthFromWeek } from '../calculations/cmgCalculations';
import { computeGrossForSimpleAndSharedHours } from '../calculations/cmgCalculationsGed';

/**
 * Reactive state for the GED CMG simulator: employer settings, contract
 * settings, wage, shared-cost split hours, validation, and conversions.
 */
export function useContractParamsGed({ toFloatForDisplay }) {
  const totalYearlyWeeks = 52;
  const maxHoursYear = 744;
  const averageMonthlySalaryRange = [1426, 2000, 4000, 6000];
  const maxNormalWorkingHours = 40;
  const maxOvertimeForty = 48;
  const maxOvertimeFifty = 50;

  // ===== Employer settings =====

  const situation = ref('couple');
  const children = ref('1');
  const childAge = ref('1');
  const averageMonthlySalary = ref('4000');
  const isCustomMonthlySalary = ref(false);
  const cmgAidToggle = ref('yes');
  const otherAidToggle = ref(false);
  const otherAid = ref('0');
  const handicapToggle = ref(false);
  const region = ref('0');
  const sharedCostToggle = ref(false);
  const sharedCost = ref('0');
  const firstEmployeeDeclaration = ref(false);

  // ===== Contract settings =====

  const monthWeek = ref('week');
  const weeklyWorkedDays = ref('5');
  const weeklyHours = ref('40');
  const yearlyWeeks = ref('52');
  const monthlyWorkedDays = ref('');
  const monthlyHours = ref('');
  const hasOvertime = ref(false);
  const hasOvertimeFifty = ref(false);
  const monthlyOvertime = ref('0');
  const monthlyOvertimeFifty = ref('0');
  const increaseOvertime = ref('25');
  const increaseOvertimeFifty = ref('50');
  const contractEndsBeforeOneYear = ref(false);

  // ===== Garde partagée hour split (populated by loadFromContract) =====

  const monthlyRegularSimpleHours = ref('');
  const monthlyRegularSharedHours = ref('');
  const monthlyOvertimeFortySimpleHours = ref('');
  const monthlyOvertimeFortySharedHours = ref('');
  const monthlyOvertimeFiftySimpleHours = ref('');
  const monthlyOvertimeFiftySharedHours = ref('');

  // ===== Wage =====

  const grossHourlyWage = ref('10,35');
  const grossHourlyWageInput = ref('10,35');
  const maximumGrossHourlyWage = ref(60.00);
  const hourlyWageSetFromInput = ref(false);
  const isHourlyRateInRange = ref(true);

  // ===== Server data =====

  const netRatios = ref({});
  const socialContributions = ref({});
  const minimumGrossHourlyWage = ref(10.35);
  const maxNetHelp = ref(15.18);
  const schedulableWeeks = ref(null);
  const weeksWorked = ref(null);

  // ===== UI state =====

  const isPublic = ref(false);
  const loadContractLabel = ref("Charger les infos d'un contrat");
  const slider = ref(null);

  // ===== Parsed values =====

  const childrenI = computed(() => Number(children.value));
  const grossHourlyWageF = computed(() => parseFloat(grossHourlyWage.value.replace(',', '.')));
  const monthlyWorkedDaysF = computed(() => parseFloat(monthlyWorkedDays.value.replace(',', '.')));
  const monthlyHoursF = computed(() => parseFloat(monthlyHours.value.replace(',', '.')));
  const weeklyWorkedDaysF = computed(() => parseFloat(weeklyWorkedDays.value.replace(',', '.')));
  const weeklyHoursF = computed(() => parseFloat(weeklyHours.value.replace(',', '.')));
  const increaseOvertimeI = computed(() => Number(increaseOvertime.value));
  const increaseOvertimeFiftyI = computed(() => Number(increaseOvertimeFifty.value));
  const otherAidF = computed(() => otherAidToggle.value ? parseFloat(otherAid.value.replace(',', '.')) : 0);
  const averageMonthlySalaryParsed = computed(() => parseFloat(averageMonthlySalary.value.replace(/\s/g, '').replace(',', '.')));
  const sharedCostF = computed(() => parseFloat(sharedCost.value.replace(',', '.')));

  const yearlyWeeksF = computed(() => {
    const result = parseFloat(yearlyWeeks.value.replace(',', '.'));
    return contractEndsBeforeOneYear.value ? result : Math.floor(result);
  });

  // ===== Net ratios =====

  const netRatioForRegion = computed(() => netRatios.value[region.value] || { label: '', classic: 0, detax: 0 });
  const netRatioClassic = computed(() => netRatioForRegion.value.classic);
  const netRatioDetax = computed(() => netRatioForRegion.value.detax);
  const regionFrench = computed(() => netRatioForRegion.value.label);

  // ===== Situation =====

  const childTooOld = computed(() => situation.value !== 'single' ? parseInt(childAge.value, 10) > 2 : childAge.value === '4');
  const cmgNotAvailable = computed(() => childTooOld.value || cmgAidToggle.value === 'no');
  const cmgUnavailable = computed(() => childTooOld.value || cmgAidToggle.value === 'no');
  const situationFrench = computed(() => situation.value === 'single' ? 'parent seul' : 'en couple');
  const childAgeFrench = computed(() => ({
    1: 'moins de 3 ans',
    2: '3 à 5 ans',
    3: '6 à 11 ans',
    4: '12 ans ou plus',
  }[childAge.value]));
  const handicapToggleInfos = computed(() => {
    return handicapToggle.value
      ? '<p>Le montant est majoré de 30% quand votre enfant est en situation d\'handicap.</p>'
      : '';
  });
  const averageMonthlySalaries = computed(() => {
    return isCustomMonthlySalary.value ? [averageMonthlySalaryParsed.value] : averageMonthlySalaryRange;
  });

  // ===== Shared cost =====

  const sharedCostRatio = computed(() => sharedCostToggle.value ? sharedCostF.value / 100 : 1);
  const dividedGrossHourlyWage = computed(() => {
    return sharedCostToggle.value
      ? sharedCostF.value / 100 * grossHourlyWageF.value
      : grossHourlyWageF.value;
  });
  const gedSharedHourSplitLoaded = computed(() => {
    const simple = parseFloat((monthlyRegularSimpleHours.value || '0').replace(',', '.')) || 0;
    const shared = parseFloat((monthlyRegularSharedHours.value || '0').replace(',', '.')) || 0;
    return simple > 0 || shared > 0;
  });

  // ===== Overtime =====

  const weeklyOvertimeFiftyFloat = computed(() => hasOvertimeFifty.value ? weeklyHoursF.value - maxOvertimeForty : 0);
  const weeklyOvertimeFloat = computed(() => {
    return hasOvertime.value ? weeklyHoursF.value - maxNormalWorkingHours - weeklyOvertimeFiftyFloat.value : 0;
  });
  const monthlyOvertimeForty = computed(() => {
    return isMonth.value
      ? parseFloat(monthlyOvertime.value.replace(',', '.'))
      : (weeklyOvertimeFloat.value / weeklyWorkedDaysF.value * monthlyWorkedDaysF.value);
  });
  const monthlyOvertimeFiftyComputed = computed(() => {
    return isMonth.value
      ? parseFloat(monthlyOvertimeFifty.value.replace(',', '.'))
      : (weeklyOvertimeFiftyFloat.value / weeklyWorkedDaysF.value * monthlyWorkedDaysF.value);
  });
  const monthlyOvertimeFloat = computed(() => monthlyOvertimeForty.value + monthlyOvertimeFiftyComputed.value);

  // ===== Validation =====

  const isWeeklyWorkedDays = computed(() => weeklyWorkedDays.value !== '' && weeklyWorkedDaysF.value >= 0 && weeklyWorkedDaysF.value <= 7);
  const isWeeklyHours = computed(() => weeklyHours.value !== '' && weeklyHoursF.value >= 0 && weeklyHoursF.value <= maxOvertimeFifty);
  const isYearlyWeeks = computed(() => yearlyWeeks.value !== '' && yearlyWeeksF.value <= totalYearlyWeeks);
  const isMonthlyHours = computed(() => monthlyHours.value !== '' && monthlyHoursF.value >= 0 && monthlyHoursF.value <= maxHoursYear);
  const isMonthlyWorkedDays = computed(() => monthlyWorkedDays.value !== '' && monthlyWorkedDaysF.value >= 0 && monthlyWorkedDaysF.value <= 31);
  const isMonthlyOvertime = computed(() => {
    if (!hasOvertime.value && !hasOvertimeFifty.value) return true;
    const fortyValid = !hasOvertime.value || (monthlyOvertime.value !== '' && monthlyOvertimeForty.value >= 0);
    const fiftyValid = !hasOvertimeFifty.value || (monthlyOvertimeFifty.value !== '' && monthlyOvertimeFiftyComputed.value >= 0);
    return fortyValid && fiftyValid && monthlyOvertimeFloat.value <= 140;
  });
  const isFullYear = computed(() => parseInt(yearlyWeeks.value, 10) === totalYearlyWeeks);
  const isWeek = computed(() => monthWeek.value === 'week');
  const isMonth = computed(() => monthWeek.value === 'month');

  const isCalculable = computed(() => {
    if (monthWeek.value === 'month') {
      if (!isMonthlyHours.value || !isMonthlyWorkedDays.value) return false;
      if ((hasOvertime.value || hasOvertimeFifty.value) && !isMonthlyOvertime.value) return false;
      if ((hasOvertime.value || hasOvertimeFifty.value) && monthlyOvertimeFloat.value > monthlyHoursF.value) return false;
      if (!isWeeklyHours.value || !isWeeklyWorkedDays.value || !isYearlyWeeks.value) return false;
    } else if (monthWeek.value === 'week') {
      if (!isWeeklyHours.value || !isWeeklyWorkedDays.value || !isYearlyWeeks.value) return false;
    }
    return monthWeek.value !== 'other';
  });

  // ===== Wage computeds =====

  const regularHours = computed(() => monthlyHoursF.value - ((hasOvertime.value || hasOvertimeFifty.value) ? monthlyOvertimeFloat.value : 0));

  // Hours declared by this family on Pajemploi: in garde partagée each family
  // only declares its share of the hours, at the employee's full hourly rate.
  function splitHours(...hourRefs) {
    return hourRefs.reduce((sum, hours) => sum + (parseFloat((hours.value || '0').replace(',', '.')) || 0), 0);
  }

  function familyShareOf(simpleHours, sharedHours) {
    return simpleHours + sharedHours * sharedCostRatio.value;
  }

  const familyRegularHours = computed(() => {
    if (sharedCostToggle.value && gedSharedHourSplitLoaded.value) {
      return familyShareOf(splitHours(monthlyRegularSimpleHours), splitHours(monthlyRegularSharedHours));
    }
    return regularHours.value * sharedCostRatio.value;
  });

  const familyOvertimeHours = computed(() => {
    if (!hasOvertime.value && !hasOvertimeFifty.value) return 0;

    if (sharedCostToggle.value && gedSharedHourSplitLoaded.value) {
      return familyShareOf(
        splitHours(monthlyOvertimeFortySimpleHours, monthlyOvertimeFiftySimpleHours),
        splitHours(monthlyOvertimeFortySharedHours, monthlyOvertimeFiftySharedHours),
      );
    }
    return monthlyOvertimeFloat.value * sharedCostRatio.value;
  });

  const familyMonthlyHours = computed(() => familyRegularHours.value + familyOvertimeHours.value);

  function grossForSimpleAndSharedHours(simpleHours, sharedHours) {
    return computeGrossForSimpleAndSharedHours(simpleHours, sharedHours, grossHourlyWageF.value, sharedCostRatio.value);
  }

  const grossMonthlyWageRegular = computed(() => {
    if (sharedCostToggle.value && gedSharedHourSplitLoaded.value) {
      const simple = parseFloat(monthlyRegularSimpleHours.value.replace(',', '.')) || 0;
      const shared = parseFloat(monthlyRegularSharedHours.value.replace(',', '.')) || 0;
      return grossForSimpleAndSharedHours(simple, shared);
    }
    return regularHours.value * dividedGrossHourlyWage.value;
  });

  const grossMonthlyOvertimeWage = computed(() => {
    if (!hasOvertime.value && !hasOvertimeFifty.value) return 0;

    if (sharedCostToggle.value && gedSharedHourSplitLoaded.value) {
      const simple = (parseFloat(monthlyOvertimeFortySimpleHours.value.replace(',', '.')) || 0) +
        (parseFloat(monthlyOvertimeFiftySimpleHours.value.replace(',', '.')) || 0);
      const shared = (parseFloat(monthlyOvertimeFortySharedHours.value.replace(',', '.')) || 0) +
        (parseFloat(monthlyOvertimeFiftySharedHours.value.replace(',', '.')) || 0);
      return grossForSimpleAndSharedHours(simple, shared);
    }

    return monthlyOvertimeFloat.value * dividedGrossHourlyWage.value;
  });

  const grossMonthlyOvertimeWagePremium = computed(() => {
    if (!hasOvertime.value && !hasOvertimeFifty.value) return 0;

    const rate = grossHourlyWageF.value;
    const ratio = sharedCostRatio.value;

    if (sharedCostToggle.value && gedSharedHourSplitLoaded.value) {
      let monthlyOvertimeIncrease = 0;
      const otFortySimple = parseFloat(monthlyOvertimeFortySimpleHours.value.replace(',', '.')) || 0;
      const otFortyShared = parseFloat(monthlyOvertimeFortySharedHours.value.replace(',', '.')) || 0;
      const otFiftySimple = parseFloat(monthlyOvertimeFiftySimpleHours.value.replace(',', '.')) || 0;
      const otFiftyShared = parseFloat(monthlyOvertimeFiftySharedHours.value.replace(',', '.')) || 0;

      monthlyOvertimeIncrease += otFortySimple * rate * increaseOvertimeI.value / 100;
      monthlyOvertimeIncrease += otFortyShared * rate * ratio * increaseOvertimeI.value / 100;
      monthlyOvertimeIncrease += otFiftySimple * rate * increaseOvertimeFiftyI.value / 100;
      monthlyOvertimeIncrease += otFiftyShared * rate * ratio * increaseOvertimeFiftyI.value / 100;

      return monthlyOvertimeIncrease;
    }

    let monthlyOvertimeIncrease = monthlyOvertimeForty.value * dividedGrossHourlyWage.value * increaseOvertimeI.value / 100;
    monthlyOvertimeIncrease += monthlyOvertimeFiftyComputed.value * dividedGrossHourlyWage.value * increaseOvertimeFiftyI.value / 100;
    return monthlyOvertimeIncrease;
  });

  const netHourlyWage = computed(() => grossHourlyWageF.value * netRatioClassic.value);

  // ===== Week/Month conversion =====

  function monthFromWeek() {
    const result = computeMonthFromWeek({
      weeklyWorkedDays: weeklyWorkedDaysF.value,
      weeklyHours: weeklyHoursF.value,
      yearlyWeeks: yearlyWeeksF.value,
      hasOvertime: hasOvertime.value || hasOvertimeFifty.value,
      weeklyOvertimeFloat: weeklyOvertimeFloat.value + weeklyOvertimeFiftyFloat.value,
    });

    monthlyWorkedDays.value = isWeeklyWorkedDays.value && isYearlyWeeks.value
      ? toFloatForDisplay(result.monthlyWorkedDays, 4)
      : '';
    monthlyHours.value = isWeeklyHours.value && yearlyWeeks.value
      ? toFloatForDisplay(result.monthlyHours, 4)
      : '';

    if (hasOvertime.value) {
      const fortyResult = computeMonthFromWeek({
        weeklyWorkedDays: weeklyWorkedDaysF.value,
        weeklyHours: weeklyHoursF.value,
        yearlyWeeks: yearlyWeeksF.value,
        hasOvertime: true,
        weeklyOvertimeFloat: weeklyOvertimeFloat.value,
      });
      monthlyOvertime.value = isNaN(fortyResult.monthlyOvertime) ? '0' : toFloatForDisplay(fortyResult.monthlyOvertime, 4);
    } else {
      monthlyOvertime.value = '0';
    }

    if (hasOvertimeFifty.value) {
      const fiftyResult = computeMonthFromWeek({
        weeklyWorkedDays: weeklyWorkedDaysF.value,
        weeklyHours: weeklyHoursF.value,
        yearlyWeeks: yearlyWeeksF.value,
        hasOvertime: true,
        weeklyOvertimeFloat: weeklyOvertimeFiftyFloat.value,
      });
      monthlyOvertimeFifty.value = isNaN(fiftyResult.monthlyOvertime) ? '0' : toFloatForDisplay(fiftyResult.monthlyOvertime, 4);
    } else {
      monthlyOvertimeFifty.value = '0';
    }
  }

  function weekFromMonth() {
    if (isMonthlyOvertime.value) {
      const weeklyOvertime = isWeeklyWorkedDays.value && isMonthlyWorkedDays.value
        ? ((monthlyOvertimeFloat.value / monthlyWorkedDaysF.value) * weeklyWorkedDaysF.value)
        : 0;

      weeklyHours.value = isMonthlyHours.value && isWeeklyWorkedDays.value && isMonthlyWorkedDays.value
        ? toFloatForDisplay(
          ((monthlyHoursF.value - monthlyOvertimeFloat.value) / monthlyWorkedDaysF.value * weeklyWorkedDaysF.value) + weeklyOvertime,
          2,
        )
        : '40';
    } else if (isMonthlyHours.value && isWeeklyWorkedDays.value && isMonthlyWorkedDays.value) {
      weeklyHours.value = toFloatForDisplay(
        monthlyHoursF.value / monthlyWorkedDaysF.value * weeklyWorkedDaysF.value,
        2,
      );
    } else {
      weeklyHours.value = '40';
    }
  }

  function toggleMW() {
    if (monthWeek.value === 'month') monthFromWeek();
    if (monthWeek.value === 'week') weekFromMonth();
  }

  // ===== Slider helpers =====

  function setGrossHourlyWage(value) {
    grossHourlyWage.value = value;
    grossHourlyWageInput.value = value;
    if (slider.value) {
      const numericValue = parseFloat(value.replace(',', '.'));
      if (!isNaN(numericValue)) slider.value.set(numericValue);
    }
  }

  function setGrossHourlyWageFromSlider(numericValue) {
    const formatted = toFloatForDisplay(numericValue, 2);
    grossHourlyWage.value = formatted;
    grossHourlyWageInput.value = formatted;
  }

  function adjustHourlyRate(adjustment) {
    let hourlyWage = grossHourlyWageF.value;
    hourlyWage = adjustment === 'increase' ? hourlyWage + 0.01 : hourlyWage - 0.01;
    if (slider.value) slider.value.set(hourlyWage);
    const formatted = toFloatForDisplay(hourlyWage, 2);
    grossHourlyWage.value = formatted;
    grossHourlyWageInput.value = formatted;
  }

  function updateHourlyWage() {
    hourlyWageSetFromInput.value = true;
    const hourlyWage = parseFloat(grossHourlyWageInput.value.replace(',', '.'));
    if (hourlyWage <= maximumGrossHourlyWage.value || hourlyWage >= minimumGrossHourlyWage.value) {
      grossHourlyWage.value = toFloatForDisplay(hourlyWage, 2);
      if (slider.value) slider.value.set(hourlyWage);
    }
    hourlyWageSetFromInput.value = false;
  }

  function checkErrors() {
    const toggleInvalid = (selector, isInvalid) => {
      document.querySelectorAll(selector).forEach(el => el.classList.toggle('is-invalid', isInvalid));
    };
    toggleInvalid('.wrapper_input.simulator_weekly_worked_days .input-group', !isWeeklyWorkedDays.value);
    toggleInvalid('.wrapper_input.simulator_weekly_hours .input-group', !isWeeklyHours.value);
    toggleInvalid('.wrapper_input.simulator_yearly_weeks .input-group', !isYearlyWeeks.value);
    toggleInvalid('.wrapper_input.simulator_monthly_worked_days .input-group', !isMonthlyWorkedDays.value);
    toggleInvalid('.wrapper_input.simulator_monthly_hours .input-group', !isMonthlyHours.value);
    toggleInvalid('.wrapper_input.simulator_monthly_overtime .input-group', !isMonthlyOvertime.value);
  }

  function print() {
    import('./printSection.js').then((module) =>
      module.printPageSection('#cmg-simulator__print', ['cmg-simulator']),
    );
  }

  function initialize(apiData) {
    netRatios.value = apiData.net_ratios;
    socialContributions.value = apiData.social_contributions;
    minimumGrossHourlyWage.value = apiData.minimum_gross_hourly_wage;
    if (apiData.max_net_help) maxNetHelp.value = apiData.max_net_help;
    if (apiData.maximum_gross_hourly_wage) maximumGrossHourlyWage.value = apiData.maximum_gross_hourly_wage;
  }

  function loadFromContract(data) {
    childAge.value = data.child_age;
    if (data.children) children.value = data.children;
    region.value = data.region;
    contractEndsBeforeOneYear.value = data.contract_ends_before_one_year;
    yearlyWeeks.value = data.yearly_weeks;
    schedulableWeeks.value = data.schedulable_weeks;
    weeksWorked.value = data.weeks_worked;
    monthWeek.value = data.month_week;
    hasOvertime.value = data.has_overtime;
    hasOvertimeFifty.value = data.has_overtime_fifty;
    loadContractLabel.value = data.load_contract_label;
    if (data.increase_overtime) increaseOvertime.value = data.increase_overtime;
    if (data.increase_overtime_fifty) increaseOvertimeFifty.value = data.increase_overtime_fifty;
    if (data.shared_cost_toggle) sharedCostToggle.value = data.shared_cost_toggle;
    if (data.shared_cost) sharedCost.value = data.shared_cost;
    if (data.maximum_gross_hourly_wage) {
      maximumGrossHourlyWage.value = parseFloat(data.maximum_gross_hourly_wage.replace(',', '.'));
    }

    if (data.monthly_regular_simple_hours) monthlyRegularSimpleHours.value = data.monthly_regular_simple_hours;
    if (data.monthly_regular_shared_hours) monthlyRegularSharedHours.value = data.monthly_regular_shared_hours;
    if (data.monthly_overtime_forty_simple_hours) monthlyOvertimeFortySimpleHours.value = data.monthly_overtime_forty_simple_hours;
    if (data.monthly_overtime_forty_shared_hours) monthlyOvertimeFortySharedHours.value = data.monthly_overtime_forty_shared_hours;
    if (data.monthly_overtime_fifty_simple_hours) monthlyOvertimeFiftySimpleHours.value = data.monthly_overtime_fifty_simple_hours;
    if (data.monthly_overtime_fifty_shared_hours) monthlyOvertimeFiftySharedHours.value = data.monthly_overtime_fifty_shared_hours;

    setTimeout(() => {
      monthlyWorkedDays.value = data.monthly_worked_days;
      monthlyHours.value = data.monthly_hours;
      monthlyOvertime.value = data.monthly_overtime;
      monthlyOvertimeFifty.value = data.monthly_overtime_fifty;
      setGrossHourlyWage(data.gross_hourly_wage);
    });
  }

  return {
    situation,
    children,
    childAge,
    averageMonthlySalary,
    isCustomMonthlySalary,
    cmgAidToggle,
    otherAidToggle,
    otherAid,
    handicapToggle,
    region,
    sharedCostToggle,
    sharedCost,
    firstEmployeeDeclaration,
    monthWeek,
    weeklyWorkedDays,
    weeklyHours,
    yearlyWeeks,
    monthlyWorkedDays,
    monthlyHours,
    hasOvertime,
    hasOvertimeFifty,
    monthlyOvertime,
    monthlyOvertimeFifty,
    increaseOvertime,
    increaseOvertimeFifty,
    contractEndsBeforeOneYear,
    monthlyRegularSimpleHours,
    monthlyRegularSharedHours,
    monthlyOvertimeFortySimpleHours,
    monthlyOvertimeFortySharedHours,
    monthlyOvertimeFiftySimpleHours,
    monthlyOvertimeFiftySharedHours,
    grossHourlyWage,
    grossHourlyWageInput,
    maximumGrossHourlyWage,
    hourlyWageSetFromInput,
    isHourlyRateInRange,
    netRatios,
    socialContributions,
    minimumGrossHourlyWage,
    maxNetHelp,
    schedulableWeeks,
    weeksWorked,
    isPublic,
    loadContractLabel,
    slider,
    childrenI,
    grossHourlyWageF,
    monthlyWorkedDaysF,
    monthlyHoursF,
    weeklyWorkedDaysF,
    weeklyHoursF,
    increaseOvertimeI,
    increaseOvertimeFiftyI,
    otherAidF,
    averageMonthlySalaryParsed,
    sharedCostF,
    yearlyWeeksF,
    netRatioForRegion,
    netRatioClassic,
    netRatioDetax,
    regionFrench,
    childTooOld,
    cmgNotAvailable,
    cmgUnavailable,
    situationFrench,
    childAgeFrench,
    handicapToggleInfos,
    averageMonthlySalaries,
    sharedCostRatio,
    dividedGrossHourlyWage,
    gedSharedHourSplitLoaded,
    weeklyOvertimeFiftyFloat,
    weeklyOvertimeFloat,
    monthlyOvertimeForty,
    monthlyOvertimeFiftyComputed,
    monthlyOvertimeFloat,
    maxNormalWorkingHours,
    maxOvertimeForty,
    maxOvertimeFifty,
    isWeeklyWorkedDays,
    isWeeklyHours,
    isYearlyWeeks,
    isMonthlyHours,
    isMonthlyWorkedDays,
    isMonthlyOvertime,
    isFullYear,
    isWeek,
    isMonth,
    isCalculable,
    regularHours,
    familyRegularHours,
    familyOvertimeHours,
    familyMonthlyHours,
    grossForSimpleAndSharedHours,
    grossMonthlyWageRegular,
    grossMonthlyOvertimeWage,
    grossMonthlyOvertimeWagePremium,
    netHourlyWage,
    monthFromWeek,
    weekFromMonth,
    toggleMW,
    setGrossHourlyWage,
    setGrossHourlyWageFromSlider,
    adjustHourlyRate,
    updateHourlyWage,
    checkErrors,
    print,
    initialize,
    loadFromContract,
  };
}
